/* eslint-disable */
import EventEmitter from 'events';
import autoBind from 'auto-bind';
import XDate from 'xdate';
import { Device } from '@twilio/voice-sdk';
import { config } from 'configuration';
import { mobileBridge } from 'provider/mobileBridge';
import { get } from 'utils';
import set from 'lodash/set';

export class TwilioWebRTC extends EventEmitter {
    constructor() {
        super();
        autoBind(this);

        this.startTime = null;
        this.currentTime = 0;
        this.duration = 0;
        this.decibels = 0;
        this.metaData = null;
        this._calltimer = null;
        this.activeCall = null;

        // Handle mobile webrtc
        mobileBridge.on('webrtc:event', (...args) => {
            this.emit(...args);
        });
    }

    resetDevice() {
        if (this.device) {
            this.device.off('registered', this.onReady);
            this.device.disconnectAll();
            this.device = null;
        }
        if (this.connection) {
            this.connection.off('accept', this.onConnected);
            this.connection.off('disconnect', this.onDisconnected);
        }
    }

    get connection() {
        return this.activeCall ? this.activeCall : null;
    }

    onReady() {
        console.log("Twilio device is registered.")
        this.emit('ready');
    }

    onConnected(connection) {
        const { customParameters = {} } = connection;
        this._callTimer = setInterval(() => {
            if (!this.startTime) return; 
            const currentTime = this.startTime.diffSeconds(new XDate());
            this.duration = currentTime;
            this.currentTime = currentTime;
            this.emit('timeChange', this.currentTime);
        }, 500);
        console.log("Twilio call accepted.")
        connection.mute(customParameters.get("listenOnly") === 'true');
       
        const pc = get(connection, 'mediaStream.version.pc');
        if (pc && pc.getReceivers()) {
            pc.getReceivers().forEach(receiver => {
                receiver.playoutDelayHint = 1;
                receiver.track.enabled = true;
            });
        }

        if(customParameters.get("listenOnly") === 'true'){
            if (pc && pc.getSenders()) {
                pc.getSenders().forEach(sender => {
                    sender.track.enabled = false;
                });
            }
            if(pc) {
                pc.ontrack = function(ev) {
                    if (ev.streams && ev.streams[0]) {
                        const audioEl = document.createElement("AUDIO")
                        audioEl.srcObject = ev.streams[0];
                        audioEl.play();
                        audioEl.muted = false;
                    }
                }
            }
        }

        this.startTime = new XDate();
        this.emit('connected');
    }

    onDisconnected() {
        console.log("Twilio call disconnected.")
        clearInterval(this._callTimer);
        this.startTime = null;
        this.currentTime = 0;
        this.duration = 0;
        this.decibels = 0;
        this.metaData = null;
        this._calltimer = null;
        this.emit('disconnected');
    }

    authenticate(data, pcConfig) {
        const { call_id: event_id, token: event_token } = data;
        return fetch(`${config.STREAMS_ENDPOINT}/token?event_id=${event_id}&event_token=${event_token}`, {
            mode: 'cors'
        })
            .then(r => r.json())
            .then(({ token }) => {
                this.device = new Device(token, {
                    logLevel: 1, // possible values: 0=trace, 1=debug, 2=info, 3=warn, 4=error, 5=silent
                    debug: true, // enable debug logs
                    rtcConfiguration: pcConfig,
                });

                // add event handlers
                this.device.on('error', (error) => {
                    console.log('Twilio device failed to register: ', error)
                });
                this.device.on('registered', this.onReady);

                // register device
                this.device.register();
            });
    }

    async connect(data, listenOnly, metaData) {
        const { call_id: event_id, token: event_token, mode, ...rest } = data;
        this.metaData = metaData;
        const params = {
            event_id,
            event_token,
            listenOnly: !!listenOnly,
            // Old stuff uses mode, new stuff uses role so pass both
            role: mode === 'agent' ? 'agent' : 'attendee',
            mode,
            auth: config.STREAMS_AUTH_TOKEN,
            ...data
        };
        const call = await this.device.connect({
            params:params,
        });

        this.activeCall = call;

        call.on('accept', this.onConnected);
        call.on('disconnect', this.onDisconnected);
    }

    reset() {
        this.resetDevice();
    }

    mute() {
        if (this.connection) {
            this.connection.mute(true);
            this.emit('muted', this.connection.isMuted());
        }
    }

    unmute() {
        if (this.connection) {
            const { customParameters = {} } = this.connection;
            if (customParameters.listenOnly === 'false') {
                this.connection.mute(false);
                this.emit('muted', this.connection.isMuted());
            }
        }
    }

    sendDTMF(tone, duration) {
        if (this.connection) {
            this.connection.sendDigits(String(tone));
        }
    }
}
